import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { NotificationBell } from '../NotificationBell';
import PWAInstallButton from '../PWAInstallButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  PaperPlaneTilt, Receipt, Plus, SignOut, User,
  Heart, ClockCounterClockwise, ArrowDownLeft, Rss, GearSix, ChartBar,
  ChatCircleDots, Globe, Code, List, X, ShieldCheck, UserPlus, ArrowUpRight, CaretRight, CalendarBlank,
  Lightning, Flask, CaretDown, Sun, Moon
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export function DashboardHeader({ mobileMenuOpen, setMobileMenuOpen, onLogout }) {
  const { user } = useAuth();
  const { t, isDark, toggleTheme } = useTheme();

  const ghostBtnStyle = {
    color: isDark ? '#e5e7eb' : '#0F172A',
  };

  const dropdownContentStyle = {
    background: t.surface,
    borderColor: t.dividerBorder,
    color: t.textPrimary,
  };

  return (
    <header
      className="backdrop-blur-xl sticky top-0 z-50"
      style={{
        background: t.headerBgNew,
        borderBottom: `1px solid ${t.headerBorder}`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="header-logo">
          <img
            src={LOGO_URL}
            alt="Divispend"
            className="h-7 w-auto"
            style={{ filter: isDark ? 'none' : 'invert(1) hue-rotate(180deg)' }}
          />
          <span className="hidden sm:inline font-semibold tracking-tight text-sm" style={{ color: t.textPrimary }}>Divispend</span>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          <Link
            to="/settings/2fa"
            className="px-2.5 py-1 text-[11px] rounded-full font-medium transition-colors flex items-center gap-1.5"
            style={{
              background: t.accentSoft,
              color: t.accentText,
              border: `1px solid ${isDark ? 'rgba(0,229,117,0.25)' : 'rgba(0,229,117,0.35)'}`,
            }}
            data-testid="soc2-badge"
          >
            <ShieldCheck className="w-3 h-3" weight="bold" />
            SOC 2 Verified
          </Link>
          <PWAInstallButton />
          <button
            onClick={toggleTheme}
            className="h-9 w-9 rounded-lg flex items-center justify-center transition-colors"
            style={{
              background: t.surfaceAlt,
              border: `1px solid ${t.dividerBorder}`,
              color: t.textPrimary,
            }}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-4 h-4" weight="bold" /> : <Moon className="w-4 h-4" weight="bold" />}
          </button>
          <NotificationBell />
          {user?.role === 'admin' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-lg h-9 px-3" style={{ color: t.accentText }} data-testid="admin-menu-trigger">
                  <Lightning className="w-3.5 h-3.5 mr-1.5" weight="fill" />
                  Admin
                  <CaretDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px]" style={dropdownContentStyle}>
                <DropdownMenuLabel className="text-[10px] tracking-[0.18em] uppercase" style={{ color: t.textFaint }}>Rollout Control</DropdownMenuLabel>
                <DropdownMenuItem asChild data-testid="admin-menu-features">
                  <Link to="/admin/features" className="flex items-center gap-3 cursor-pointer">
                    <Lightning className="w-4 h-4" style={{ color: t.accent }} />
                    <div className="flex flex-col">
                      <span>Feature Flags & Phases</span>
                      <span className="text-[10px]" style={{ color: t.textFaint }}>Launch features by phase</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-menu-beta">
                  <Link to="/admin/beta" className="flex items-center gap-3 cursor-pointer">
                    <Flask className="w-4 h-4 text-[#3B82F6]" />
                    <div className="flex flex-col">
                      <span>Beta Users</span>
                      <span className="text-[10px]" style={{ color: t.textFaint }}>Manage invite codes</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background: t.dividerBorder }} />
                <DropdownMenuLabel className="text-[10px] tracking-[0.18em] uppercase" style={{ color: t.textFaint }}>Platform</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/admin/analytics" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-analytics">
                    <ChartBar className="w-4 h-4" /> Fee Analytics
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/fees" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-fees">
                    <GearSix className="w-4 h-4" /> Fee Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/users" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-users">
                    <User className="w-4 h-4" /> User Management
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/withdrawals" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-withdrawals">
                    <ArrowDownLeft className="w-4 h-4" /> Withdrawals
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/security" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-security">
                    <ShieldCheck className="w-4 h-4" /> Security & Compliance
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/domains" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-domains">
                    <Globe className="w-4 h-4" /> Domains
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/feedback" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-feedback">
                    <ChatCircleDots className="w-4 h-4" /> User Feedback
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/api" className="flex items-center gap-3 cursor-pointer" data-testid="admin-menu-api">
                    <Code className="w-4 h-4" /> API Reference
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold transition-colors"
                style={{
                  background: t.surfaceAlt,
                  border: `1px solid ${t.dividerBorder}`,
                  color: t.textPrimary,
                }}
                data-testid="header-user-avatar"
                aria-label="Account menu"
              >
                {(user?.name || user?.email || 'D').substring(0, 2).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]" style={dropdownContentStyle}>
              <DropdownMenuLabel className="text-[10px] tracking-[0.18em] uppercase" style={{ color: t.textFaint }}>Account</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/profile" className="cursor-pointer"><User className="w-4 h-4 mr-2" /> Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="cursor-pointer"><GearSix className="w-4 h-4 mr-2" /> Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/feed" className="cursor-pointer"><Rss className="w-4 h-4 mr-2" /> Feed</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/messages" className="cursor-pointer"><ChatCircleDots className="w-4 h-4 mr-2" /> Messages</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator style={{ background: t.dividerBorder }} />
              <DropdownMenuItem onClick={onLogout} className="cursor-pointer" style={{ color: '#EF4444' }} data-testid="header-logout">
                <SignOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex md:hidden items-center gap-1.5">
          <PWAInstallButton compact />
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
              background: t.surfaceAlt,
              border: `1px solid ${t.dividerBorder}`,
              color: t.textPrimary,
            }}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle-mobile"
          >
            {isDark ? <Sun className="w-4 h-4" weight="bold" /> : <Moon className="w-4 h-4" weight="bold" />}
          </button>
          <NotificationBell />
          <Button
            variant="ghost"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-lg p-2 h-9 w-9"
            style={ghostBtnStyle}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="md:hidden px-4 py-3 space-y-1 max-h-[70vh] overflow-y-auto"
          style={{ background: t.surface, borderTop: `1px solid ${t.dividerBorder}` }}
        >
          {[
            { to: '/feed', icon: Rss, label: 'Feed' },
            { to: '/favorites', icon: Heart, label: 'Favorites' },
            { to: '/messages', icon: ChatCircleDots, label: 'Messages' },
            { to: '/profile', icon: User, label: 'Profile' },
            { to: '/history', icon: ClockCounterClockwise, label: 'Transaction History' },
            { to: '/seller-analytics', icon: ChartBar, label: 'Seller Analytics' },
            { to: '/crm', icon: Rss, label: 'CRM & Contacts' },
            { to: '/invite', icon: UserPlus, label: 'Invite Friends' },
            { to: '/subscriptions', icon: CalendarBlank, label: 'Subscriptions' },
            { to: '/investor', icon: ChartBar, label: 'Investor Dashboard' },
          ].map(item => (
            <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start rounded-lg" style={{ color: t.textPrimary }} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <item.icon className="w-4 h-4 mr-3" />
                {item.label}
              </Button>
            </Link>
          ))}
          <div className="my-2 pt-2" style={{ borderTop: `1px solid ${t.dividerBorder}` }}>
            <p className="text-[10px] tracking-[0.15em] uppercase font-semibold px-3 pb-1" style={{ color: t.textFaint }}>Settings</p>
            {[
              { to: '/settings/2fa', icon: ShieldCheck, label: 'Two-Factor Auth' },
              { to: '/settings/privacy', icon: GearSix, label: 'Privacy & GDPR' },
            ].map(item => (
              <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-lg" style={{ color: t.textPrimary }} data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <item.icon className="w-4 h-4 mr-3" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </div>
          {user?.role === 'admin' && (
            <div className="my-2 pt-2" style={{ borderTop: `1px solid ${t.dividerBorder}` }}>
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold px-3 pb-1" style={{ color: t.textFaint }}>Admin</p>
              {[
                { to: '/admin/features', icon: Lightning, label: 'Feature Flags & Phases' },
                { to: '/admin/beta', icon: Flask, label: 'Beta Users' },
                { to: '/admin/analytics', icon: ChartBar, label: 'Fee Analytics' },
                { to: '/admin/fees', icon: GearSix, label: 'Fee Settings' },
                { to: '/admin/users', icon: User, label: 'User Management' },
                { to: '/admin/withdrawals', icon: ArrowDownLeft, label: 'Withdrawals' },
                { to: '/admin/security', icon: ShieldCheck, label: 'Security & Compliance' },
                { to: '/admin/domains', icon: Globe, label: 'Domains' },
                { to: '/admin/api', icon: Code, label: 'API Reference' },
                { to: '/admin/feedback', icon: ChatCircleDots, label: 'User Feedback' },
              ].map(item => (
                <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start rounded-lg" style={{ color: t.accentText }} data-testid={`mobile-nav-admin-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <item.icon className="w-4 h-4 mr-3" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </div>
          )}
          <div className="my-2 pt-2" style={{ borderTop: `1px solid ${t.dividerBorder}` }}>
            <Button variant="ghost" onClick={onLogout} className="w-full justify-start rounded-lg" style={{ color: '#EF4444' }} data-testid="mobile-nav-logout">
              <SignOut className="w-4 h-4 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

export function BalanceCard({ balance, formatCurrency }) {
  return (
    <Card className="bg-gradient-to-br from-[#22C55E] to-[#16A34A] text-white border-none rounded-sm mb-8" data-testid="balance-card">
      <CardContent className="p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/70 mb-2">Wallet Balance</p>
            <p className="text-4xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="wallet-balance">
              {formatCurrency(balance)}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/wallet"><Button className="bg-white text-[#22C55E] hover:bg-white/90 rounded-sm h-11 font-semibold" data-testid="topup-button"><Plus className="w-4 h-4 mr-2" />Add Money</Button></Link>
            <Link to="/send"><Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm h-11 font-semibold" data-testid="send-money-button"><PaperPlaneTilt className="w-4 h-4 mr-2" />Send Money</Button></Link>
            <Link to="/request"><Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm h-11 font-semibold" data-testid="request-money-button"><Receipt className="w-4 h-4 mr-2" />Request</Button></Link>
            <Link to="/wallet/withdraw"><Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm h-11 font-semibold" data-testid="withdraw-button"><ArrowDownLeft className="w-4 h-4 mr-2" />Withdraw</Button></Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function RecentTransactions({ transactions, user, formatCurrency, formatDate }) {
  return (
    <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="transactions-card">
      <CardHeader className="p-6 pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>Recent Transactions</CardTitle>
        <Link to="/history" className="text-sm text-[#22C55E] hover:underline flex items-center gap-1" data-testid="view-all-transactions">View all <CaretRight className="w-3 h-3" /></Link>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-[#999999]">
            <ClockCounterClockwise className="w-12 h-12 mx-auto mb-3 text-[#333333]" />
            <p>No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.filter(txn => txn.type !== 'platform_fee').map((txn, index) => (
              <div key={txn.transaction_id} className="flex items-center justify-between py-4 border-b border-[#333333] last:border-0" data-testid={`transaction-item-${index}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${txn.type === 'topup' || txn.to_user_id === user?.user_id ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#FF3B30]/10 text-[#FF3B30]'}`}>
                    {txn.type === 'topup' || txn.to_user_id === user?.user_id ? <ArrowDownLeft className="w-5 h-5" weight="bold" /> : <ArrowUpRight className="w-5 h-5" weight="bold" />}
                  </div>
                  <div>
                    <p className="font-medium text-white">
                      {txn.type === 'topup' ? `Added Money (${txn.payment_method || 'Stripe'})` : txn.type === 'purchase' ? 'Purchase' : txn.to_user_id === user?.user_id ? `From ${txn.from_email}` : `To ${txn.to_email}`}
                    </p>
                    <p className="text-sm text-[#999999]">{formatDate(txn.created_at)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${txn.type === 'topup' || txn.to_user_id === user?.user_id ? 'text-[#22C55E]' : 'text-white'}`}>
                    {txn.type === 'topup' || txn.to_user_id === user?.user_id ? '+' : '-'}{formatCurrency(txn.amount)}
                  </p>
                  {txn.fee > 0 && <p className="text-xs text-[#999999]">Fee: {formatCurrency(txn.fee)}</p>}
                  <Link to={`/receipt/${txn.transaction_id}`} className="text-[10px] text-[#22C55E] hover:text-[#1EA34B] transition-colors" data-testid={`dashboard-receipt-link-${index}`}>View Receipt</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function InviteCard() {
  return (
    <Card className="bg-gradient-to-br from-[#0D1F17] to-[#1A1A1A] border border-[#22C55E]/20 rounded-sm shadow-none mt-6" data-testid="invite-friends-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-sm bg-[#22C55E]/10 flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-[#22C55E]" />
          </div>
          <div>
            <p className="font-bold text-white text-sm" style={{ fontFamily: 'Cabinet Grotesk' }}>Invite Friends</p>
            <p className="text-xs text-[#999999]">Grow your network</p>
          </div>
        </div>
        <p className="text-xs text-[#666666] mb-4">Share Divispend with friends via link, email, WhatsApp, or QR code.</p>
        <Link to="/invite">
          <Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-10 font-semibold text-sm" data-testid="invite-friends-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Now
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
